import cx from 'classnames'
import { type ReactNode, useCallback } from 'react'
import {
  ScreenHeaderProvider,
  ScreenHeaderPersistentTopBar,
  ScreenHeaderBorder,
  ScreenHeaderPersistentTopBarLayout,
  ScreenHeaderPersistentTopBarTitle,
  ScreenHeaderMain,
} from '@/components/ScreenHeader'
import { IconButton } from '@/components/IconButton'
import { links } from '@/utils/link'
import { Button } from '@/components/Button'
import { SectionHeadline } from '@/components/SectionHeadline'
import { Card, CardContent } from 'components/Card'
import { Avatar } from '@/components/Avatar'
import { AutoSkeleton } from '@/components/Skeleton'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import { useFileTrigger } from '@/hooks/file-hooks'
import { AccountSubviewScreenHeaderContent } from 'features/account/components/AccountSubviewScreenHeaderContent'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateIconDecoration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import styles from './PersonalInfoView.module.css'

interface PersonalInfoViewProps {
  children?: ReactNode
}

export function PersonalInfoView({ children }: PersonalInfoViewProps) {
  const isSpacious = useIsSpacious()
  return (
    <>
      {!isSpacious && (
        <ScreenHeaderProvider fadeVariant="aggressive">
          <ScreenHeaderPersistentTopBar>
            <ScreenHeaderPersistentTopBarLayout
              left={
                <IconButton name="back" label="Back" to={links.account()} />
              }
            >
              <ScreenHeaderPersistentTopBarTitle>
                Personal information
              </ScreenHeaderPersistentTopBarTitle>
            </ScreenHeaderPersistentTopBarLayout>
          </ScreenHeaderPersistentTopBar>
          <ScreenHeaderMain>
            <AccountSubviewScreenHeaderContent
              kicker="Account"
              title="Personal information"
            />
            <ScreenHeaderBorder />
          </ScreenHeaderMain>
        </ScreenHeaderProvider>
      )}
      <main className={cx(styles.root)}>{children}</main>
    </>
  )
}

interface ProfilePanelProps {
  avatarUrl?: string
  displayName: string
  email: string | null
  phone: string | null
  isAvatarChangePending: boolean
  onEditProfilePress: () => unknown
  onAvatarChange: (file: File) => unknown
  editButtonTestId?: string
  editProfileMenuItemTestId?: string
  uploadPhotoMenuItemTestId?: string
}

export function ProfilePanel({
  avatarUrl,
  displayName,
  email,
  phone,
  isAvatarChangePending,
  onEditProfilePress,
  onAvatarChange,
  editButtonTestId,
  editProfileMenuItemTestId,
  uploadPhotoMenuItemTestId,
}: ProfilePanelProps) {
  const selectFile = useFileTrigger({
    allowMultiple: false,
    onFilesSelected: useCallback(
      (files) => onAvatarChange(files[0]),
      [onAvatarChange],
    ),
  })
  return (
    <div>
      <SectionHeadline
        action={
          <Dropdown
            title="Edit"
            trigger={
              <Button
                isDisabled={isAvatarChangePending}
                label="Edit"
                variant="text"
                iconLeft="edit"
                testId={editButtonTestId}
              />
            }
          >
            <DropdownItem
              icon="edit-name"
              onAction={onEditProfilePress}
              testId={editProfileMenuItemTestId}
            >
              Edit your profile
            </DropdownItem>

            <DropdownItem
              icon="camera"
              onAction={selectFile}
              testId={uploadPhotoMenuItemTestId}
            >
              Upload new photo
            </DropdownItem>
          </Dropdown>
        }
      >
        Your profile
      </SectionHeadline>
      <Card>
        <CardContent>
          <div className="text-center">
            <div className="flex flex-col items-center">
              <Avatar
                isSkeleton={isAvatarChangePending}
                size={64}
                subject={{ avatarUrl, displayName }}
              />
            </div>
            <h3 className="text-headline-md mt-lg">{displayName}</h3>
            {email && <p className={styles.email}>{email}</p>}

            {phone && <p className="text-body-base mt-sm">{phone}</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

interface Section7216AuthorizationsSectionProps {
  isSkeleton: boolean
  children: ReactNode
  id?: string
}

export function Section7216AuthorizationsSection({
  isSkeleton,
  children,
  id,
}: Section7216AuthorizationsSectionProps) {
  return (
    <div>
      <h2 id={id} className="text-headline-lg">
        <AutoSkeleton isSkeleton={isSkeleton}>
          Data disclosure authorizations
        </AutoSkeleton>
      </h2>
      <p className="mt-base text-body-md-light mb-2xl">
        <AutoSkeleton isSkeleton={isSkeleton}>
          View the data disclosure authorizations you've accepted.
        </AutoSkeleton>
      </p>

      {children}
    </div>
  )
}

interface TermsOfUseAgreementsSectionProps {
  isSkeleton: boolean
  children: ReactNode
  id?: string
}

export function TermsOfUseAgreementsSection({
  isSkeleton,
  children,
  id,
}: TermsOfUseAgreementsSectionProps) {
  return (
    <div>
      <h2 id={id} className="text-headline-lg">
        <AutoSkeleton isSkeleton={isSkeleton}>
          Agreements & policies
        </AutoSkeleton>
      </h2>
      <p className="mt-base text-body-md-light mb-2xl">
        <AutoSkeleton isSkeleton={isSkeleton}>
          View the terms of use agreements you've accepted.
        </AutoSkeleton>
      </p>

      {children}
    </div>
  )
}

interface TermsOfUseAgreementsEmptyProps {
  onViewTermsPress?: () => unknown
  viewTermsButtonTestId?: string
}

export function TermsOfUseAgreementsEmpty({
  onViewTermsPress,
  viewTermsButtonTestId,
}: TermsOfUseAgreementsEmptyProps) {
  return (
    <EmptyState>
      <EmptyStateIconDecoration icon="file" />
      <EmptyStateTitle>No agreements</EmptyStateTitle>
      <EmptyStateDescription>
        Once you've agreed to our terms, the acceptance record will appear here.
      </EmptyStateDescription>
      {onViewTermsPress && (
        <EmptyStateActions>
          <Button
            iconLeft="view"
            label="View terms"
            onPress={onViewTermsPress}
            testId={viewTermsButtonTestId}
          />
        </EmptyStateActions>
      )}
    </EmptyState>
  )
}

export function Section7216AuthorizationsEmpty() {
  return (
    <EmptyState>
      <EmptyStateIconDecoration icon="file" />
      <EmptyStateTitle>No authorizations</EmptyStateTitle>
      <EmptyStateDescription>
        Once you've authorized the data disclosure agreement, the acceptance
        record will appear here.
      </EmptyStateDescription>
    </EmptyState>
  )
}
